import { useState, useEffect, useMemo, useCallback } from 'react';
import { API_BASE } from '../config';
import { exportSheet, todayStamp } from '../excel';

// SKU Master -> "Summary Report": change tracker for the "Inventory File"
// sheet of "Pending Order Sku wise 2026-27.xlsx" (synced by Power Automate
// through inventory-stock.js). Two views:
//   1. File changes — what changed in EAN / Product Name / Product Status
//      between uploads (logged by backend/shared/inventoryFileChanges.js),
//      with a reviewed checkbox so it works as a to-do list.
//   2. vs SKU Master — where the current file disagrees with sku_master
//      right now, each with a button that opens the master record.
// Colours use the --acc-* theme variables, like SKUDescription.jsx.

const TYPES = {
    new_ean:        { label: 'New EAN',        color: '#16A34A', icon: '➕' },
    ean_changed:    { label: 'EAN changed',    color: '#7C3AED', icon: '🔁' },
    name_changed:   { label: 'Name changed',   color: '#2563EB', icon: '✏️' },
    status_changed: { label: 'Status changed', color: '#D97706', icon: '🏷️' },
    removed_ean:    { label: 'Removed',        color: '#DC2626', icon: '➖' },
};

const RANGES = [7, 30, 90];

function fmtWhen(s) {
    if (!s) return '—';
    const [d, t] = s.split(' ');
    const [y, m, day] = d.split('-').map(Number);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(day).padStart(2, '0')} ${months[m - 1]} ${y}${t ? ', ' + t.slice(0, 5) : ''}`;
}

export default function InventoryFileReport({ user, onClose, onOpenSku }) {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState('changes');
    const [typeFilter, setTypeFilter] = useState('');
    const [showReviewed, setShowReviewed] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE}/inventory-file-changes?days=${days}`);
            const json = await res.json();
            if (!res.ok || !json.success) throw new Error(json.message || `Server returned ${res.status}`);
            setData(json);
            setSelected(new Set());
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const changes = useMemo(() => (data?.changes || []).filter(c =>
        (!typeFilter || c.change_type === typeFilter) && (showReviewed || !c.reviewed_at)
    ), [data, typeFilter, showReviewed]);

    const mm = data?.mismatches || { not_in_master: [], name_mismatch: [], status_mismatch: [] };
    const cc = data?.change_counts || {};

    async function markReviewed(ids, reviewed = true) {
        if (!ids.length) return;
        setSaving(true);
        try {
            const res = await fetch(`${API_BASE}/inventory-file-changes?action=review`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids, reviewed, reviewed_by: user?.display_name || user?.username || null }),
            });
            if (!res.ok) throw new Error(`Server returned ${res.status}`);
            await load();
        } catch (e) {
            alert('Could not update: ' + e.message);
        } finally {
            setSaving(false);
        }
    }

    function toggle(id) {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }

    function exportTab() {
        const stamp = todayStamp();
        if (tab === 'changes') {
            exportSheet({
                headers: ['Detected', 'Change', 'EAN', 'Old EAN', 'Product Name', 'Old Value', 'New Value', 'Uploaded By', 'Reviewed By', 'Reviewed At'],
                rows: changes.map(c => [c.detected_at, TYPES[c.change_type]?.label || c.change_type, c.ean_code || '', c.old_ean || '', c.product_name || '', c.old_value || '', c.new_value || '', c.uploaded_by || '', c.reviewed_by || '', c.reviewed_at || '']),
                sheetName: 'File Changes', filename: `Inventory_File_Changes_${stamp}.xlsx`,
            });
            return;
        }
        const rows = tab === 'not_in_master' ? mm.not_in_master : tab === 'name' ? mm.name_mismatch : mm.status_mismatch;
        exportSheet({
            headers: ['EAN', 'Name in File', 'Name in SKU Master', 'Status in File', 'Status in SKU Master'],
            rows: rows.map(r => [r.ean_code, r.file_name || '', r.master_name || '', r.file_status || '', r.master_status || '']),
            sheetName: 'vs SKU Master', filename: `Inventory_File_vs_SKU_Master_${tab}_${stamp}.xlsx`,
        });
    }

    const TABS = [
        { key: 'changes',       label: 'File changes',       count: cc.unreviewed ?? 0, hint: 'unreviewed' },
        { key: 'not_in_master', label: 'Not in SKU Master',  count: mm.not_in_master.length },
        { key: 'name',          label: 'Name differs',       count: mm.name_mismatch.length },
        { key: 'status',        label: 'Status differs',     count: mm.status_mismatch.length },
    ];

    return (
        <div className="ifr-overlay" onClick={onClose}>
            <style>{CSS}</style>
            <div className="ifr-panel" onClick={e => e.stopPropagation()} role="dialog" aria-label="Inventory file summary report">
                <div className="ifr-head">
                    <div>
                        <div className="ifr-kicker">Summary Report · Change Tracker</div>
                        <h2>Inventory File Changes</h2>
                        <p>
                            <b>Pending Order Sku wise 2026-27.xlsx</b> → “Inventory File” sheet · tracks EAN, Product Name and Product Status.
                            {data?.last_upload && <> Last synced <b>{fmtWhen(data.last_upload.uploaded_at)}</b>{data.last_upload.uploaded_by ? ` by ${data.last_upload.uploaded_by}` : ''}.</>}
                        </p>
                    </div>
                    <div className="ifr-head-actions">
                        <div className="ifr-range">
                            {RANGES.map(d => (
                                <button key={d} className={days === d ? 'on' : ''} onClick={() => setDays(d)}>{d}d</button>
                            ))}
                        </div>
                        <button className="ifr-icon-btn" onClick={load} title="Refresh">⟳</button>
                        <button className="ifr-icon-btn" onClick={onClose} title="Close (Esc)">✕</button>
                    </div>
                </div>

                {error && <div className="ifr-error">Couldn't load the report: {error}</div>}

                {/* KPI tiles — clicking one filters the change log */}
                <div className="ifr-kpis">
                    <button className={`ifr-kpi${tab === 'changes' && !typeFilter ? ' on' : ''}`} style={{ '--c': '#D0202A' }}
                        onClick={() => { setTab('changes'); setTypeFilter(''); setShowReviewed(false); }}>
                        <span className="ifr-kpi-n">{loading ? '…' : cc.unreviewed ?? 0}</span>
                        <span className="ifr-kpi-l">To review</span>
                    </button>
                    {Object.entries(TYPES).map(([k, t]) => (
                        <button key={k} className={`ifr-kpi${tab === 'changes' && typeFilter === k ? ' on' : ''}`} style={{ '--c': t.color }}
                            onClick={() => { setTab('changes'); setTypeFilter(typeFilter === k ? '' : k); }}>
                            <span className="ifr-kpi-n">{loading ? '…' : cc[k] ?? 0}</span>
                            <span className="ifr-kpi-l">{t.icon} {t.label}</span>
                        </button>
                    ))}
                </div>

                <div className="ifr-tabs">
                    {TABS.map(t => (
                        <button key={t.key} className={tab === t.key ? 'on' : ''} onClick={() => setTab(t.key)}>
                            {t.label} <span className="ifr-tab-n">{loading ? '…' : t.count}</span>
                        </button>
                    ))}
                    <button className="ifr-export" onClick={exportTab} disabled={loading}>⬇ Excel</button>
                </div>

                <div className="ifr-body">
                    {loading ? <p className="ifr-empty">Loading…</p> : tab === 'changes' ? (
                        <>
                            <div className="ifr-toolbar">
                                <label className="ifr-check">
                                    <input type="checkbox" checked={showReviewed} onChange={e => setShowReviewed(e.target.checked)} /> Show reviewed
                                </label>
                                {typeFilter && <span className="ifr-chip" style={{ '--c': TYPES[typeFilter].color }}>{TYPES[typeFilter].label}<button onClick={() => setTypeFilter('')}>×</button></span>}
                                <span style={{ flex: 1 }} />
                                <button className="ifr-btn" disabled={saving || selected.size === 0} onClick={() => markReviewed([...selected])}>
                                    ✓ Mark {selected.size || ''} reviewed
                                </button>
                            </div>
                            {changes.length === 0 ? (
                                <div className="ifr-empty">
                                    {(data?.changes || []).length === 0
                                        ? <>No changes detected in the last {days} days. Each Power Automate sync is compared with the previous one; anything that changes in EAN, Product Name or Product Status shows up here.</>
                                        : <>Nothing left to review 🎉 Tick “Show reviewed” to see past changes.</>}
                                </div>
                            ) : (
                                <table className="ifr-table">
                                    <thead><tr><th style={{ width: 34 }} /><th>Detected</th><th>Change</th><th>EAN</th><th>Product</th><th>Before → After</th><th /></tr></thead>
                                    <tbody>
                                        {changes.map(c => {
                                            const t = TYPES[c.change_type] || { label: c.change_type, color: '#64748B', icon: '•' };
                                            return (
                                                <tr key={c.id} className={c.reviewed_at ? 'is-reviewed' : ''}>
                                                    <td>{!c.reviewed_at && <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} />}</td>
                                                    <td className="ifr-nowrap">{fmtWhen(c.detected_at)}</td>
                                                    <td><span className="ifr-type" style={{ '--c': t.color }}>{t.icon} {t.label}</span></td>
                                                    <td className="ifr-mono">{c.old_ean ? <><s>{c.old_ean}</s><br /></> : null}{c.ean_code || '—'}</td>
                                                    <td className="ifr-wrap">{c.product_name || '—'}</td>
                                                    <td className="ifr-wrap">
                                                        {c.change_type === 'new_ean' ? <span className="ifr-new">Added · {c.new_value || 'no status'}</span>
                                                            : c.change_type === 'removed_ean' ? <span className="ifr-old">Removed from file</span>
                                                            : <><span className="ifr-old">{c.old_value || '—'}</span> <span className="ifr-arrow">→</span> <span className="ifr-new">{c.new_value || '—'}</span></>}
                                                        {c.reviewed_at && <div className="ifr-meta">Reviewed by {c.reviewed_by || '—'} · {fmtWhen(c.reviewed_at)}</div>}
                                                    </td>
                                                    <td className="ifr-nowrap">
                                                        {c.ean_code && c.change_type !== 'removed_ean' && (
                                                            <button className="ifr-link" onClick={() => onOpenSku({ ean: c.ean_code, name: c.product_name, status: c.change_type === 'status_changed' ? c.new_value : null, exists: c.change_type !== 'new_ean' && c.change_type !== 'ean_changed' })}>
                                                                Open in master →
                                                            </button>
                                                        )}
                                                        {c.reviewed_at
                                                            ? <button className="ifr-link muted" onClick={() => markReviewed([c.id], false)}>Undo</button>
                                                            : <button className="ifr-link muted" onClick={() => markReviewed([c.id])}>✓</button>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </>
                    ) : (
                        <MismatchTable
                            kind={tab}
                            rows={tab === 'not_in_master' ? mm.not_in_master : tab === 'name' ? mm.name_mismatch : mm.status_mismatch}
                            onOpenSku={onOpenSku}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

function MismatchTable({ kind, rows, onOpenSku }) {
    if (rows.length === 0) return <div className="ifr-empty">The inventory file and SKU Master agree here. 🎉</div>;
    const intro = {
        not_in_master: 'These EANs are in the inventory file but not in SKU Master. Add them so they show up across the portal.',
        name: 'Same EAN, different product name in the file vs SKU Master (spacing and capitalisation are ignored).',
        status: 'Same EAN, different status in the file vs SKU Master (“PM” in the file counts as “Packing Material”).',
    }[kind];
    return (
        <>
            <p className="ifr-intro">{intro}</p>
            <table className="ifr-table">
                <thead>
                    <tr>
                        <th>EAN</th>
                        <th>{kind === 'not_in_master' ? 'Product (file)' : 'Name in file'}</th>
                        {kind !== 'not_in_master' && <th>Name in SKU Master</th>}
                        <th>Status in file</th>
                        {kind !== 'not_in_master' && <th>Status in SKU Master</th>}
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {rows.map(r => (
                        <tr key={r.ean_code}>
                            <td className="ifr-mono">{r.ean_code}</td>
                            <td className={`ifr-wrap${kind === 'name' ? ' ifr-new' : ''}`}>{r.file_name || '—'}</td>
                            {kind !== 'not_in_master' && <td className={`ifr-wrap${kind === 'name' ? ' ifr-old' : ''}`}>{r.master_name || '—'}</td>}
                            <td className={kind === 'status' ? 'ifr-new' : ''}>{r.file_status || '—'}</td>
                            {kind !== 'not_in_master' && <td className={kind === 'status' ? 'ifr-old' : ''}>{r.master_status || '—'}</td>}
                            <td className="ifr-nowrap">
                                <button className="ifr-link" onClick={() => onOpenSku({ ean: r.ean_code, name: r.file_name, status: r.file_status, exists: kind !== 'not_in_master' })}>
                                    {kind === 'not_in_master' ? '+ Add to master' : 'Edit in master →'}
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
}

const CSS = `
.ifr-overlay { position: fixed; inset: 0; z-index: 450; background: var(--acc-overlay-bg); backdrop-filter: blur(3px);
  display: flex; justify-content: center; align-items: flex-start; padding: 32px 20px; overflow-y: auto; animation: ifr-fade .15s ease; }
@keyframes ifr-fade { from { opacity: 0; } }
.ifr-panel { width: 100%; max-width: 1180px; background: var(--acc-surface); color: var(--acc-text); border: 1px solid var(--acc-border);
  border-radius: 18px; box-shadow: 0 24px 60px rgba(0,0,0,0.35); overflow: hidden; animation: ifr-rise .22s ease; font-family: 'Space Grotesk', system-ui, sans-serif; }
@keyframes ifr-rise { from { transform: translateY(14px); opacity: 0; } }
.ifr-head { display: flex; justify-content: space-between; gap: 18px; flex-wrap: wrap; padding: 22px 26px;
  background: linear-gradient(135deg, #2A2140 0%, #1A1529 60%, #120D1D 100%); color: #fff; }
.ifr-kicker { font-size: 11px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; color: #A48FD1; }
.ifr-head h2 { margin: 4px 0 6px; font-size: 22px; font-weight: 600; }
.ifr-head p { margin: 0; font-size: 13px; color: #B2AAC4; max-width: 640px; }
.ifr-head p b { color: #fff; }
.ifr-head-actions { display: flex; gap: 8px; align-items: flex-start; }
.ifr-range { display: flex; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.18); border-radius: 9px; overflow: hidden; }
.ifr-range button { border: 0; background: transparent; color: #C9BFE0; padding: 8px 12px; font: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer; }
.ifr-range button.on { background: #A48FD1; color: #1A1529; }
.ifr-icon-btn { width: 36px; height: 36px; border-radius: 9px; border: 1px solid rgba(255,255,255,0.18); background: rgba(255,255,255,0.08); color: #fff; font-size: 16px; cursor: pointer; }
.ifr-icon-btn:hover { background: rgba(255,255,255,0.16); }
.ifr-error { margin: 14px 26px 0; padding: 10px 14px; border-radius: 8px; background: var(--acc-danger-bg); color: var(--acc-danger); font-weight: 600; font-size: 13px; }
.ifr-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 10px; padding: 18px 26px 6px; }
.ifr-kpi { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; padding: 12px 14px; border-radius: 12px; cursor: pointer; font: inherit; text-align: left;
  background: var(--acc-page-bg); color: var(--acc-text); border: 1.5px solid var(--acc-border); border-top: 4px solid var(--c); transition: transform .15s ease, box-shadow .15s ease; }
.ifr-kpi:hover { transform: translateY(-2px); box-shadow: 0 8px 18px color-mix(in srgb, var(--c) 25%, transparent); }
.ifr-kpi.on { background: color-mix(in srgb, var(--c) 12%, var(--acc-surface)); border-color: var(--c); }
.ifr-kpi-n { font-size: 24px; font-weight: 800; color: var(--c); line-height: 1.1; }
.ifr-kpi-l { font-size: 11.5px; font-weight: 700; color: var(--acc-text-muted); text-transform: uppercase; letter-spacing: .05em; }
.ifr-tabs { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; padding: 12px 26px 0; border-bottom: 1px solid var(--acc-border); }
.ifr-tabs > button:not(.ifr-export) { border: 0; background: transparent; color: var(--acc-text-muted); padding: 10px 14px; font: inherit; font-weight: 700; font-size: 13.5px; cursor: pointer;
  border-bottom: 3px solid transparent; margin-bottom: -1px; }
.ifr-tabs > button.on { color: var(--acc-primary); border-bottom-color: var(--acc-primary); }
.ifr-tab-n { display: inline-block; min-width: 22px; padding: 1px 7px; margin-left: 4px; border-radius: 999px; font-size: 11.5px; background: var(--acc-badge-muted-bg); color: var(--acc-text); }
.ifr-export { margin-left: auto; margin-bottom: 8px; border: 0; border-radius: 8px; padding: 8px 14px; background: #5C7E60; color: #fff; font: inherit; font-weight: 700; font-size: 12.5px; cursor: pointer; }
.ifr-body { padding: 16px 26px 26px; max-height: calc(100vh - 360px); overflow: auto; }
.ifr-toolbar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
.ifr-check { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; color: var(--acc-text-muted); cursor: pointer; }
.ifr-chip { display: inline-flex; align-items: center; gap: 6px; padding: 4px 6px 4px 10px; border-radius: 999px; font-size: 12px; font-weight: 700;
  color: var(--c); background: color-mix(in srgb, var(--c) 14%, transparent); }
.ifr-chip button { border: 0; background: transparent; color: var(--c); cursor: pointer; font-weight: 800; }
.ifr-btn { border: 0; border-radius: 8px; padding: 8px 14px; background: var(--acc-primary); color: #fff; font: inherit; font-weight: 700; font-size: 12.5px; cursor: pointer; }
.ifr-btn:disabled { opacity: .45; cursor: default; }
.ifr-intro { margin: 0 0 12px; font-size: 13px; color: var(--acc-text-muted); font-style: italic; }
.ifr-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.ifr-table th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: .05em; padding: 9px 10px;
  background: var(--acc-grid-header-bg); color: var(--acc-grid-header-text); position: sticky; top: -16px; z-index: 1; }
.ifr-table td { padding: 9px 10px; border-bottom: 1px solid var(--acc-border); vertical-align: top; }
.ifr-table tbody tr:hover { background: var(--acc-primary-light); }
.ifr-table tr.is-reviewed { opacity: .55; }
.ifr-mono { font-family: ui-monospace, Consolas, monospace; font-size: 13px; white-space: nowrap; }
.ifr-mono s { color: var(--acc-danger); }
.ifr-wrap { min-width: 200px; line-height: 1.4; }
.ifr-nowrap { white-space: nowrap; }
.ifr-type { display: inline-block; padding: 3px 9px; border-radius: 999px; font-size: 11.5px; font-weight: 700; white-space: nowrap;
  color: var(--c); background: color-mix(in srgb, var(--c) 14%, transparent); }
.ifr-old { color: var(--acc-danger); }
.ifr-new { color: var(--acc-success); font-weight: 600; }
.ifr-arrow { color: var(--acc-text-muted); }
.ifr-meta { font-size: 11px; color: var(--acc-text-muted); margin-top: 3px; }
.ifr-link { border: 0; background: transparent; color: var(--acc-primary); font: inherit; font-weight: 700; font-size: 12.5px; cursor: pointer; padding: 2px 6px; }
.ifr-link:hover { text-decoration: underline; }
.ifr-link.muted { color: var(--acc-text-muted); }
.ifr-empty { padding: 36px 12px; text-align: center; color: var(--acc-text-muted); font-size: 14px; line-height: 1.6; }
`;
